import { useCallback } from 'react';

import { backerKeys, useConsumerSubscribeToCause } from '@givebacks/api/backer';
import { useShowCauseW_Selector } from '@givebacks/api/core';
import { useQueryClient } from '@tanstack/react-query';
import { getErrorMessage_v2 } from 'core/Utils';
import { notifications } from 'ui';

import { CauseCardBase } from './CauseCardBase';
import { CauseStatusButton } from './CauseStatusButton';

const CauseCard = ({
  causeId,
  status,
  token,
  fullWidth = false,
  onUnsubscribe,
}: {
  causeId?: string;
  status?: string;
  token?: string;
  fullWidth?: boolean;
  onUnsubscribe?: () => void;
}) => {
  const { data: cause } = useShowCauseW_Selector({ uuid: causeId });
  const queryClient = useQueryClient();
  const { mutate: subscribeToCause, isPending: isLoading } = useConsumerSubscribeToCause();

  const onSubscribe = useCallback(() => {
    subscribeToCause(
      { causeId, token: token },
      {
        onSuccess: () => {
          notifications.show({
            message: `You are now subscribed to communications for ${cause?.name || 'this cause'}`,
            variant: 'success',
          });
          const key = backerKeys.consumer.backer.subscriptions.all;
          void queryClient.invalidateQueries({ queryKey: key });
        },
        onError: (error) => {
          notifications.show({
            message: getErrorMessage_v2(error),
            variant: 'error',
          });
        },
      }
    );
  }, [subscribeToCause, causeId, token, cause?.name, queryClient]);

  return (
    <CauseCardBase
      causeId={causeId}
      fullWidth={fullWidth}
      containerProps={{ mt: 4 }}
      ctaButton={
        <CauseStatusButton
          status={status}
          causeId={causeId}
          token={token}
          fullWidth={fullWidth}
          onUnsubscribe={onUnsubscribe}
          isLoading={isLoading}
          onSubscribe={onSubscribe}
        />
      }
    />
  );
};

export { CauseCard, CauseCardBase };
